import { Refresh, StarOutline } from "@mui/icons-material";
import {
    accordionClasses,
    accordionSummaryClasses,
    Box,
    Button,
    css,
    Grid,
    IconButton,
    styled,
    Typography,
    useTheme,
} from "@mui/material";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "app/redux-store-interactions";
import { Accordion } from "components/accordion";
import { AccordionDetails } from "components/accordionDetails";
import { AccordionSummary } from "components/accordionSummary";
import { FeatureGroupKey, featureGroups, FeatureType, Widget, WidgetKey } from "config/features";
import { useRefreshAndRestoreState } from "hooks/useRefreshAndRestoreState";
import {
    selectEnabledWidgets,
    selectFavoriteWidgets,
    selectIsOnline,
    selectLockedWidgets,
    selectNewProjectVersionStatus,
    selectWidgets,
} from "slices/explorer";

import { WidgetBottomScrollBox } from "../../../components/scrollBox";
import { WidgetMenuButtonWrapper } from "../../../components/widgetMenuButtonWrapper";
import { widgetSorting } from "../sorting";
import { groupSorting } from "../sorting";

const sortedFeatureGroups = Object.values(featureGroups).sort((a, b) => {
    const idxA = groupSorting.indexOf(a.key);
    const idxB = groupSorting.indexOf(b.key);

    return (idxA === -1 ? groupSorting.length : idxA) - (idxB === -1 ? groupSorting.length : idxB);
});

export function Root({
    handleClick,
    currentWidget,
    expandedGroupKey,
    setExpandedGroupKey,
}: {
    currentWidget: WidgetKey | undefined;
    handleClick: (key: WidgetKey) => () => void;
    expandedGroupKey: FeatureGroupKey | null;
    setExpandedGroupKey: (group: FeatureGroupKey | null) => void;
}) {
    const theme = useTheme();
    const { t } = useTranslation();
    const scrollBoxRef = useRef<HTMLDivElement | null>(null);

    const enabledWidgets = useAppSelector(selectEnabledWidgets);
    const lockedWidgets = useAppSelector(selectLockedWidgets);
    const activeWidgets = useAppSelector(selectWidgets);
    const favoriteWidgets = useAppSelector(selectFavoriteWidgets);
    const isOnline = useAppSelector(selectIsOnline);
    const scrollIntoViewTimeout = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        if (expandedGroupKey) {
            const groupNode = scrollBoxRef.current?.querySelector(`#widget-group-${expandedGroupKey}`);
            if (groupNode) {
                groupNode.scrollIntoView({ behavior: "smooth" });
                if (scrollIntoViewTimeout.current) {
                    clearTimeout(scrollIntoViewTimeout.current);
                }
                scrollIntoViewTimeout.current = setTimeout(() => {
                    // Scroll one more time after panel expanded in case it's not in the view
                    // We keep first scroll (before timer) because it's annoying if we have to wait 200ms every time
                    groupNode.scrollIntoView({ behavior: "smooth" });
                    scrollIntoViewTimeout.current = undefined;
                }, 200);
            }
        }
    }, [expandedGroupKey]);

    const sortAndFilterWidgets = (widgets: Widget[]) =>
        widgets
            .filter((widget) => !lockedWidgets.includes(widget.key))
            .sort((a, b) => {
                const idxA = widgetSorting.indexOf(a.key);
                const idxB = widgetSorting.indexOf(b.key);

                return (idxA === -1 ? widgetSorting.length : idxA) - (idxB === -1 ? widgetSorting.length : idxB);
            });

    const sortedEnabledWidgets = sortAndFilterWidgets(enabledWidgets);

    const widgetGroups = sortedFeatureGroups
        .map((group) => {
            return {
                groupKey: group.key,
                groupName: t(group.nameKey),
                GroupIcon: group.Icon,
                widgets:
                    group.key === featureGroups.favorites.key
                        ? sortedEnabledWidgets.filter((w) => favoriteWidgets.includes(w.key))
                        : sortedEnabledWidgets.filter(
                              (widget) =>
                                  (widget.type === FeatureType.Widget || widget.type === FeatureType.AdminWidget) &&
                                  "groups" in widget &&
                                  widget.groups.includes(group.key as never),
                          ),
            };
        })
        .filter((g) => g.groupKey === "favorites" || g.widgets.length > 0);

    return (
        <>
            <Box position="relative">
                <Box
                    boxShadow={theme.customShadows.widgetHeader}
                    sx={{ height: 5, width: 1, mt: "-5px" }}
                    position="absolute"
                />
            </Box>

            <WidgetBottomScrollBox flexGrow={1} py={2} ref={scrollBoxRef}>
                <RefreshNewVersionButton />
                {widgetGroups.map(({ groupKey, groupName, GroupIcon, widgets }) => {
                    return (
                        <GroupAccordion
                            key={groupKey}
                            expanded={groupKey === expandedGroupKey}
                            onChange={(_, newExpanded) => {
                                setExpandedGroupKey(newExpanded ? (groupKey as FeatureGroupKey) : null);
                            }}
                            id={`widget-group-${groupKey}`}
                        >
                            <GroupAccordionSummary>
                                <GroupIcon />
                                <Typography sx={{ ml: 1 }}>{groupName}</Typography>
                            </GroupAccordionSummary>
                            <AccordionDetails>
                                {widgets.length === 0 ? (
                                    <Box textAlign="center" color="grey" m={2}>
                                        {groupKey === featureGroups.favorites.key ? (
                                            <>
                                                {t("noFavoriteWidgets1") + " "}
                                                <StarOutline sx={{ verticalAlign: "text-bottom" }} />{" "}
                                                {" " + t("noFavoriteWidgets2")}
                                            </>
                                        ) : (
                                            <>{t("noWidgets")}</>
                                        )}
                                    </Box>
                                ) : (
                                    <Grid container wrap="wrap" spacing={1} data-test="widget-list">
                                        {widgets.map(({ Icon, nameKey, key, type, ...widget }) => {
                                            const activeCurrent = key === currentWidget;
                                            const activeElsewhere = !activeCurrent && activeWidgets.includes(key);
                                            const unavailable = !isOnline && "offline" in widget && !widget.offline;

                                            return (
                                                <Grid sx={{ mb: 1 }} xs={4} item key={type + key}>
                                                    <WidgetMenuButtonWrapper
                                                        activeCurrent={activeCurrent}
                                                        activeElsewhere={activeElsewhere || unavailable}
                                                        onClick={unavailable ? undefined : handleClick(key)}
                                                    >
                                                        <IconButton
                                                            disabled={activeElsewhere || unavailable}
                                                            size="large"
                                                        >
                                                            <Icon />
                                                        </IconButton>
                                                        <Typography textAlign={"center"}>{t(nameKey)}</Typography>
                                                    </WidgetMenuButtonWrapper>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                )}
                            </AccordionDetails>
                        </GroupAccordion>
                    );
                })}
            </WidgetBottomScrollBox>
        </>
    );
}

function RefreshNewVersionButton() {
    const { t } = useTranslation();
    const newProjectVersionStatus = useAppSelector(selectNewProjectVersionStatus);
    const show = newProjectVersionStatus.available && newProjectVersionStatus.dismissed;
    const refresh = useRefreshAndRestoreState();

    if (!show) {
        return null;
    }

    return (
        <Box sx={{ display: "grid", placeItems: "center" }}>
            <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={refresh}
                startIcon={<Refresh />}
                sx={{ mb: 1 }}
            >
                {t("newProjectVersionMessageShort")}
            </Button>
        </Box>
    );
}

const GroupAccordion = styled(Accordion)(
    () => css`
        &.${accordionClasses.root} {
            &.${accordionClasses.expanded} {
                margin: 0;
            }
        }
    `,
);

const GroupAccordionSummary = styled(AccordionSummary)(
    () => css`
        &::after {
            left: 0;
            right: 0;
        }

        &.${accordionSummaryClasses.expanded}, &.${accordionSummaryClasses.disabled} {
            &::after {
                border-bottom: none;
                opacity: 0.2;
            }
        }
    `,
);
